/**
 * Table des constructions.
 *
 * Progression voulue: on commence par la cabane de plage et la cabane (bois
 * seulement), puis l'igloo si on est monte au glacier, ensuite chalet et
 * maisonnette (bois + pierre), et la villa comme objectif de fin. Les nouveaux
 * habitables s'inserent dans la meme echelle: la tente ne coute que de la fibre
 * et un peu de bois, la cabane dans l'arbre demande de la feuille, la grange est
 * un gros chantier de bois et le phare l'ouvrage de pierre le plus ambitieux.
 *
 * Les amenagements suivent la meme regle: un modele qu'on pose en serie (chemin,
 * route, cloture, haie, torche) coute quelques unites, un modele unique qui
 * decore une propriete (fontaine, moulin, statue, tour de guet) coute cher.
 *
 * Les emprises sont en metres et correspondent au modele 3D pose dessus
 * (client/render/batiments.ts).
 */
import type { BatimentDef, BatimentKind } from '../types/monde.ts'
import { PISTE_AEROPORT, TAILLE_AEROGARE } from './aeroports.ts'
import { BASSINS_PARC, TAILLE_PARC } from './parc-aquatique.ts'

export const BATIMENTS: Record<BatimentKind, BatimentDef> = {
  // ------------------------------------------------------------ habitations
  cabane: {
    kind: 'cabane',
    nom: 'Cabane',
    feminin: true,
    icone: '🛖',
    cout: { bois: 20 },
    taille: { l: 5, p: 5 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  chalet: {
    kind: 'chalet',
    nom: 'Chalet',
    icone: '🏡',
    cout: { bois: 45, pierre: 12, feuille: 6 },
    taille: { l: 7, p: 6 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  maisonnette: {
    kind: 'maisonnette',
    nom: 'Maisonnette',
    feminin: true,
    icone: '🏠',
    cout: { bois: 60, pierre: 28, fibre: 10 },
    taille: { l: 8, p: 7 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  villa: {
    kind: 'villa',
    nom: 'Villa',
    feminin: true,
    icone: '🏘️',
    cout: { bois: 80, pierre: 40, minerai: 8 },
    taille: { l: 12, p: 9 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  villa_etage: {
    kind: 'villa_etage',
    nom: 'Villa à étage',
    feminin: true,
    icone: '🏢',
    // Plus chere que la villa, et de loin: on paie une seconde dalle, un
    // acrotere qui fait le tour, un bureau ferme et un spa.
    cout: { bois: 120, pierre: 70, minerai: 20, fibre: 15 },
    taille: { l: 14, p: 11 },
    habitable: true,
    // LE SEUL modele a porter un etage. Tout le code qui en parle passe par ce
    // drapeau, jamais par le nom du modele: le jour ou une seconde villa a
    // etage arrivera, il n'y aura rien a retrouver.
    etage: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  hotel: {
    kind: 'hotel',
    nom: 'Hôtel',
    icone: '🏨',
    // Le plus gros chantier du catalogue, et de loin: trois dalles, deux
    // chambres, une salle a manger vitree, un salon de lecture, un ascenseur,
    // un bassin et sa cascade. Il DOIT couter plus que la villa a etage.
    cout: { bois: 190, pierre: 120, minerai: 40, fibre: 25 },
    // Plus grand que la villa a etage (14 x 11), c'est la commande. 15 x 12 et
    // pas davantage: le zoo a appris qu'une emprise se MESURE contre le vrai
    // terrain — a 22 x 16 il ne se posait nulle part en foret.
    // tests/unit/hotel.test.ts compare sa frequence de pose a celle de la
    // villa a etage.
    taille: { l: 15, p: 12 },
    habitable: true,
    etage: true,
    // DEUX niveaux au-dessus du sol. C'est ce nombre qui donne l'ascenseur:
    // voir `accesEtage` dans shared/sim/batiments.ts.
    niveaux: 2,
    couleurs: ['murs', 'toit', 'porte'],
  },
  igloo: {
    kind: 'igloo',
    nom: 'Igloo',
    icone: '🧊',
    cout: { glace: 30, pierre: 6 },
    taille: { l: 5, p: 5 },
    habitable: true,
    couleurs: ['murs', 'porte'],
  },
  cabane_plage: {
    kind: 'cabane_plage',
    nom: 'Cabane de plage',
    feminin: true,
    icone: '⛱️',
    cout: { bois: 12, fibre: 6, sable: 8 },
    taille: { l: 4, p: 4 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  cabane_collations: {
    kind: 'cabane_collations',
    nom: 'Cabane à collations',
    feminin: true,
    icone: '🍦',
    cout: { bois: 18, fibre: 8, sable: 6 },
    taille: { l: 4, p: 3 },
    // On ne rentre pas: on se sert au comptoir. Pas de porte, pas de lit.
    habitable: false,
    couleurs: [],
  },
  tente: {
    kind: 'tente',
    nom: 'Tente',
    feminin: true,
    icone: '🏕️',
    cout: { fibre: 14, bois: 6 },
    taille: { l: 4.5, p: 4.5 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  cabane_arbre: {
    kind: 'cabane_arbre',
    nom: 'Cabane dans l’arbre',
    feminin: true,
    icone: '🌳',
    cout: { bois: 38, feuille: 12, fibre: 8 },
    taille: { l: 5, p: 5 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  grange: {
    kind: 'grange',
    nom: 'Grange',
    feminin: true,
    icone: '🏚️',
    cout: { bois: 95, pierre: 20, fibre: 12 },
    taille: { l: 9, p: 7 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  phare: {
    kind: 'phare',
    nom: 'Phare',
    icone: '🗼',
    cout: { pierre: 70, bois: 30, minerai: 14 },
    taille: { l: 5, p: 5 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  musee: {
    kind: 'musee',
    nom: 'Musée',
    icone: '🖼️',
    // Un batiment de village, comme l'ecole, et de la meme classe de prix: on
    // n'en batit qu'un, et il demande d'avoir deja miné.
    cout: { pierre: 60, bois: 45, minerai: 10 },
    taille: { l: 11, p: 8 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  bibliotheque: {
    kind: 'bibliotheque',
    nom: 'Bibliothèque',
    feminin: true,
    icone: '📚',
    cout: { bois: 65, pierre: 30, feuille: 14 },
    taille: { l: 9, p: 7 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  zoo: {
    kind: 'zoo',
    nom: 'Zoo',
    icone: '🦁',
    // On achete d'un coup une enceinte ET trois enclos clotures: le prix se
    // situe entre l'ecole et la villa a etage.
    cout: { bois: 95, pierre: 45, fibre: 24, minerai: 6 },
    // L'EMPRISE CONTIENT LES ENCLOS. Ce n'est plus une aire que le joueur ferme
    // a la cloture (voir shared/sim/zoo.ts): les enclos font partie du modele,
    // donc ils tiennent dans sa taille.
    // 15 x 11 m, et pas un metre de plus. Il a d'abord fait 22 x 16, ce qui
    // etait CONFORTABLE sur le papier et impossible en jeu: `validerPlacement`
    // refuse un noeud de ressource sous l'emprise, et un balayage du monde a
    // mesure ZERO emplacement en foret. A cette taille-ci il se pose aussi
    // souvent que la villa a etage, le plus gros ouvrage existant. Un batiment
    // qu'on ne peut pas poser est un batiment qui n'existe pas — la lecon du
    // « geste qui n'existe qu'au clavier ».
    taille: { l: 15, p: 11 },
    habitable: true,
    // LE ZOO EST CLOS. Son emprise est ceinte d'un mur avec une seule entree,
    // large, au milieu de la facade -z; les enclos, eux, sont des cloisons
    // interieures (voir PLAN_ZOO dans shared/data/zoo.ts). Un animal installe
    // dans un enclos n'en sort donc pas, et un visiteur circule librement dans
    // l'allee — c'est exactement ce qu'on essayait d'obtenir a la cloture.
    porte: 3,
    couleurs: ['murs', 'toit', 'porte'],
  },
  garage: {
    kind: 'garage',
    nom: 'Garage',
    icone: '🅿️',
    // Un abri, pas une maison: du bois, de la pierre pour la dalle et quelques
    // minerais pour la porte. Il coute nettement moins qu'une voiture, sinon
    // personne ne le batirait pour la ranger.
    cout: { bois: 40, pierre: 20, minerai: 6 },
    // Une voiture fait 2 x 4.2 m: l'emprise lui laisse un demi-metre de chaque
    // cote et de quoi la contourner a pied.
    taille: { l: 5, p: 6.4 },
    // « Habitable » veut dire ICI: on y entre, ses murs se resolvent par
    // `resoudreMurs` et son emprise n'est PAS barree dans la grille de
    // navigation. C'est exactement ce qu'il faut a un garage — sans quoi la
    // voiture serait arretee net par sa propre dalle.
    habitable: true,
    // TROIS METRES DE PORTE. Une porte de maison en fait 1,1: la voiture, large
    // de 2 m, serait restee plantee devant son garage pendant que l'infobulle
    // proposait d'entrer.
    porte: 3.2,
    couleurs: ['murs', 'toit', 'porte'],
  },
  /*
   * LES TROIS AEROPORTS. Meme emprise, meme etage, meme piste, memes gestes:
   * ils ne different que par le prix et par l'allure (voir shared/data/
   * aeroports.ts). Un avion se pose sur n'importe lequel — c'est ce que
   * « fonctionnellement identiques » veut dire, et c'est ce qui permet de
   * relier deux villages par les airs.
   *
   * Le prix est celui d'un gros chantier: on ne bâtit pas un aeroport avant
   * d'avoir mine, et il en faut au moins un AVANT le premier avion.
   */
  aerodrome: {
    kind: 'aerodrome',
    nom: 'Aérodrome',
    icone: '🛩️',
    // Le moins cher: une aerogare de bois, un toit de tole. Il ne demande pas
    // un gramme de minerai — c'est la porte d'entree de l'aviation.
    cout: { bois: 150, pierre: 60, fibre: 35 },
    taille: { ...TAILLE_AEROGARE },
    habitable: true,
    etage: true,
    piste: { ...PISTE_AEROPORT },
    couleurs: ['murs', 'toit', 'porte'],
  },
  aeroport: {
    kind: 'aeroport',
    nom: 'Aéroport',
    icone: '✈️',
    cout: { bois: 120, pierre: 150, minerai: 35, fibre: 25 },
    taille: { ...TAILLE_AEROGARE },
    habitable: true,
    etage: true,
    piste: { ...PISTE_AEROPORT },
    couleurs: ['murs', 'toit', 'porte'],
  },
  aeroport_verre: {
    kind: 'aeroport_verre',
    nom: 'Aéroport de verre',
    icone: '🛬',
    // Le verre se fait avec du SABLE, et c'est ce qui donne enfin une grande
    // raison de creuser la plage.
    cout: { bois: 90, pierre: 120, sable: 60, minerai: 55, fibre: 25 },
    taille: { ...TAILLE_AEROGARE },
    habitable: true,
    etage: true,
    piste: { ...PISTE_AEROPORT },
    couleurs: ['murs', 'toit', 'porte'],
  },

  mine: {
    kind: 'mine',
    nom: 'Mine',
    feminin: true,
    icone: '⛏️',
    /*
     * ELLE NE COUTE PAS UN SEUL MINERAI, et c'est tout son propos: on la batit
     * justement parce qu'on n'en a plus. Un chevalement de bois, une dalle de
     * pierre et des cordages — trois matieres qui repoussent, donc une porte de
     * sortie qui ne se referme jamais. La chercher en minerai serait la meme
     * faute que le batiment qu'on ne peut pas poser: il existerait sans etre
     * atteignable.
     */
    cout: { bois: 75, pierre: 45, fibre: 15 },
    // Une galerie: plus longue que large, on y descend en enfilade jusqu'au
    // front de taille. Plus petite que l'ecole — elle doit se poser dans la
    // montagne, ou le terrain est etroit et casse.
    taille: { l: 7, p: 9 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  ecole: {
    kind: 'ecole',
    nom: 'École',
    feminin: true,
    icone: '🏫',
    // Un batiment de village, pas une cabane: elle coute autant qu'une
    // maisonnette, et on n'en batit qu'une.
    cout: { bois: 70, pierre: 35, fibre: 12 },
    taille: { l: 10, p: 8 },
    habitable: true,
    couleurs: ['murs', 'toit', 'porte'],
  },
  parc_aquatique: {
    kind: 'parc_aquatique',
    nom: 'Parc aquatique',
    icone: '🎢',
    /*
     * Le deuxieme plus gros chantier du catalogue, derriere l'hotel: un grand
     * bassin de pierre, un pavillon, trois glissades et leur passerelle, trois
     * fontaines. Le SABLE y pese lourd — le fond d'un bassin et les toboggans
     * de verre en demandent, comme l'aeroport de verre, et c'est une raison de
     * plus de creuser la plage.
     */
    cout: { bois: 120, pierre: 140, sable: 45, minerai: 28, fibre: 30 },
    // 18 x 10 m, MESURE contre le vrai monde et arbres compris: voir
    // `TAILLE_PARC` dans shared/data/parc-aquatique.ts, qui porte le tableau
    // des huit tailles essayees. C'est le batiment le plus LARGE du jeu, et il
    // se pose plus souvent que l'hotel — la barre qu'on s'est donnee.
    taille: { ...TAILLE_PARC },
    // ON Y ENTRE: son emprise n'est donc pas barree dans la grille de
    // navigation, et ses murs passent par `resoudreMurs`. « Habitable » veut
    // dire « on y entre », jamais « on y habite » — voir l'ecole et le zoo.
    habitable: true,
    // Une entree large, comme celle du zoo: on entre en groupe dans un parc, et
    // une porte de maison au bord d'un bassin est un endroit ou l'on se coince.
    porte: 2.6,
    // LA PASSERELLE DES GLISSADES est un niveau, et un seul: on y monte par
    // l'escalier (c'est `accesEtage` qui le deduit du nombre de niveaux).
    etage: true,
    couleurs: ['murs', 'toit', 'porte'],
    // OU L'ON NAGE. C'est la donnee qui le dit, plus jamais le nom du modele.
    bassins: BASSINS_PARC,
  },

  // ------------------------------------------------------- eau et jardin
  piscine: {
    kind: 'piscine',
    nom: 'Piscine',
    feminin: true,
    icone: '🏊',
    cout: { pierre: 25, sable: 10 },
    taille: { l: 6, p: 4 },
    habitable: false,
    couleurs: ['toit'],
    /*
     * ON Y NAGE, et c'est desormais ecrit ici plutot que dans `dansPiscine`.
     *
     * La boite couvre l'emprise ENTIERE (demi-dimensions 0,5), donc la regle
     * rendue est mot pour mot celle d'avant — `dansEmprise(b, x, z, -0.35)` —
     * a ceci pres qu'elle vient maintenant de la table. Le parc aquatique
     * passe par la meme, et le prochain modele d'eau aussi.
     */
    bassins: [{ x: 0, z: 0, dl: 0.5, dp: 0.5 }],
  },
  jardin: {
    kind: 'jardin',
    nom: 'Jardin',
    icone: '🌷',
    cout: { bois: 6, fibre: 4, feuille: 2 },
    taille: { l: 3, p: 3 },
    habitable: false,
    couleurs: ['murs', 'toit'],
    // Exactement ce que le jardin donnait quand c'etait ecrit en dur dans le
    // client: le deplacer en donnee ne change rien pour qui y cueille deja.
    recolte: {
      jours: 1,
      gains: [
        { item: 'baie', min: 2, max: 4 },
        { item: 'fruit', min: 1, max: 2 },
        { item: 'fibre', min: 1, max: 1, chance: 0.3 },
      ],
    },
  },
  potager: {
    kind: 'potager',
    nom: 'Potager',
    icone: '🥬',
    cout: { bois: 8, fibre: 6, feuille: 4 },
    taille: { l: 4, p: 3 },
    habitable: false,
    couleurs: ['murs'],
    // Plus grand et plus cher que le jardin: il nourrit davantage.
    recolte: {
      jours: 1,
      gains: [
        { item: 'champignon', min: 2, max: 3 },
        { item: 'baie', min: 1, max: 3 },
        { item: 'feuille', min: 1, max: 2 },
      ],
    },
  },
  serre: {
    kind: 'serre',
    nom: 'Serre',
    feminin: true,
    icone: '🪴',
    cout: { bois: 18, pierre: 8, fibre: 12 },
    taille: { l: 4, p: 3 },
    habitable: false,
    couleurs: ['murs'],
    // Fermee et chauffee: elle donne les fruits fins et repousse en deux tiers
    // de journee la ou les autres mettent une journee pleine. C'est ce qui paie
    // ses 18 bois et ses 12 fibres — sinon elle ne serait qu'un potager cher.
    recolte: {
      jours: 0.65,
      gains: [
        { item: 'fruit', min: 2, max: 4 },
        { item: 'figue', min: 1, max: 2 },
        { item: 'champignon', min: 1, max: 2 },
        { item: 'noix_coco', min: 1, max: 1, chance: 0.5 },
      ],
    },
  },
  fontaine: {
    kind: 'fontaine',
    nom: 'Fontaine',
    feminin: true,
    icone: '⛲',
    cout: { pierre: 32, minerai: 4 },
    taille: { l: 3, p: 3 },
    habitable: false,
    couleurs: ['murs'],
  },
  puits: {
    kind: 'puits',
    nom: 'Puits',
    icone: '🪣',
    cout: { pierre: 20, bois: 10 },
    taille: { l: 2.2, p: 2.2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  ruche: {
    kind: 'ruche',
    nom: 'Ruche',
    feminin: true,
    icone: '🐝',
    cout: { bois: 10, fibre: 4 },
    taille: { l: 1.2, p: 1.2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },

  // --------------------------------------------- cloisonnement, circulation
  cloture: {
    kind: 'cloture',
    nom: 'Clôture',
    feminin: true,
    icone: '🚧',
    cout: { bois: 2 },
    taille: { l: 2, p: 0.4 },
    habitable: false,
    couleurs: ['murs'],
  },
  haie: {
    kind: 'haie',
    nom: 'Haie',
    feminin: true,
    icone: '🌿',
    cout: { feuille: 4, fibre: 2 },
    taille: { l: 2, p: 0.7 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  muret: {
    kind: 'muret',
    nom: 'Muret',
    icone: '🧱',
    cout: { pierre: 6 },
    taille: { l: 2, p: 0.5 },
    habitable: false,
    couleurs: ['murs'],
  },
  portail: {
    kind: 'portail',
    nom: 'Portail',
    icone: '🚪',
    cout: { bois: 9, minerai: 2 },
    taille: { l: 2.6, p: 0.5 },
    habitable: false,
    couleurs: ['murs', 'porte'],
  },
  chemin_pierre: {
    kind: 'chemin_pierre',
    nom: 'Chemin de pierre',
    icone: '🪨',
    cout: { pierre: 2 },
    taille: { l: 1.6, p: 1.6 },
    habitable: false,
    couleurs: ['murs'],
  },
  route: {
    kind: 'route',
    nom: 'Route',
    feminin: true,
    icone: '🛣️',
    cout: { pierre: 5, sable: 3 },
    taille: { l: 4, p: 4 },
    habitable: false,
    couleurs: ['murs'],
  },
  pont_bois: {
    kind: 'pont_bois',
    nom: 'Pont de bois',
    icone: '🌉',
    cout: { bois: 24, fibre: 4 },
    taille: { l: 3, p: 6 },
    habitable: false,
    couleurs: ['murs'],
  },
  quai: {
    kind: 'quai',
    nom: 'Quai',
    icone: '⚓',
    cout: { bois: 14 },
    taille: { l: 2.5, p: 6 },
    habitable: false,
    couleurs: ['murs'],
  },
  escalier: {
    kind: 'escalier',
    nom: 'Escalier',
    icone: '🪜',
    cout: { pierre: 12, bois: 4 },
    taille: { l: 2, p: 3 },
    habitable: false,
    couleurs: ['murs'],
  },

  // ------------------------------------------------------------- mobilier
  banc: {
    kind: 'banc',
    nom: 'Banc',
    icone: '🪑',
    cout: { bois: 6 },
    taille: { l: 1.8, p: 0.8 },
    habitable: false,
    couleurs: ['murs'],
  },
  table_picnic: {
    kind: 'table_picnic',
    nom: 'Table de pique-nique',
    feminin: true,
    icone: '🍽️',
    cout: { bois: 14 },
    taille: { l: 2.2, p: 2 },
    habitable: false,
    couleurs: ['murs'],
  },
  hamac: {
    kind: 'hamac',
    nom: 'Hamac',
    icone: '🛌',
    cout: { fibre: 10, bois: 6 },
    taille: { l: 3, p: 1.4 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  parasol: {
    kind: 'parasol',
    nom: 'Parasol',
    icone: '🌂',
    cout: { bois: 4, fibre: 8 },
    taille: { l: 2.4, p: 2.4 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  chaise_longue: {
    kind: 'chaise_longue',
    nom: 'Chaise longue',
    feminin: true,
    icone: '🛋️',
    cout: { bois: 5, fibre: 4 },
    taille: { l: 1, p: 2.2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  balancoire: {
    kind: 'balancoire',
    nom: 'Balançoire',
    feminin: true,
    icone: '🎠',
    cout: { bois: 14, fibre: 4 },
    taille: { l: 2.6, p: 1.8 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  toboggan: {
    kind: 'toboggan',
    nom: 'Toboggan',
    icone: '🛝',
    cout: { bois: 12, minerai: 4 },
    taille: { l: 1.6, p: 3.4 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  bac_sable: {
    kind: 'bac_sable',
    nom: 'Bac à sable',
    icone: '🏖️',
    cout: { bois: 8, sable: 14 },
    taille: { l: 3, p: 3 },
    habitable: false,
    couleurs: ['murs'],
  },
  barbecue: {
    kind: 'barbecue',
    nom: 'Barbecue',
    icone: '🍖',
    cout: { pierre: 12, minerai: 4 },
    taille: { l: 1.6, p: 1 },
    habitable: false,
    couleurs: ['murs'],
  },
  feu_camp: {
    kind: 'feu_camp',
    nom: 'Feu de camp',
    icone: '🔥',
    cout: { bois: 8, pierre: 6 },
    taille: { l: 1.8, p: 1.8 },
    habitable: false,
    couleurs: ['murs'],
  },

  // ------------------------------------------------------------- eclairage
  lampadaire: {
    kind: 'lampadaire',
    nom: 'Lampadaire',
    icone: '🏮',
    cout: { bois: 3, pierre: 2, minerai: 1 },
    taille: { l: 0.6, p: 0.6 },
    habitable: false,
    couleurs: ['murs'],
  },
  torche: {
    kind: 'torche',
    nom: 'Torche',
    feminin: true,
    icone: '🪔',
    cout: { bois: 3, fibre: 2 },
    taille: { l: 0.5, p: 0.5 },
    habitable: false,
    couleurs: ['murs'],
  },
  guirlande: {
    kind: 'guirlande',
    nom: 'Guirlande',
    feminin: true,
    icone: '💡',
    cout: { bois: 5, fibre: 4, minerai: 2 },
    taille: { l: 3.4, p: 0.5 },
    habitable: false,
    couleurs: ['murs'],
  },

  // --------------------------------------------------- utilitaire et decor
  coffre: {
    kind: 'coffre',
    nom: 'Coffre',
    icone: '📦',
    cout: { bois: 12, minerai: 2 },
    taille: { l: 1.3, p: 1 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  atelier: {
    kind: 'atelier',
    nom: 'Atelier',
    icone: '🛠️',
    cout: { bois: 26, pierre: 10, minerai: 5 },
    taille: { l: 3, p: 2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  four_pain: {
    kind: 'four_pain',
    nom: 'Four à pain',
    icone: '🍞',
    cout: { pierre: 24, bois: 8 },
    taille: { l: 2.2, p: 2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  moulin: {
    kind: 'moulin',
    nom: 'Moulin',
    icone: '🌾',
    cout: { bois: 50, pierre: 28, fibre: 12 },
    taille: { l: 5, p: 5 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  tour_guet: {
    kind: 'tour_guet',
    nom: 'Tour de guet',
    feminin: true,
    icone: '🔭',
    cout: { bois: 34, pierre: 10, fibre: 6 },
    taille: { l: 3, p: 3 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  epouvantail: {
    kind: 'epouvantail',
    nom: 'Épouvantail',
    icone: '🎃',
    cout: { bois: 4, fibre: 6, feuille: 2 },
    taille: { l: 1.2, p: 1.2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  statue: {
    kind: 'statue',
    nom: 'Statue',
    feminin: true,
    icone: '🗿',
    cout: { pierre: 28, minerai: 3 },
    taille: { l: 1.6, p: 1.6 },
    habitable: false,
    couleurs: ['murs'],
  },
  totem: {
    kind: 'totem',
    nom: 'Totem',
    icone: '🪵',
    cout: { bois: 18, minerai: 2 },
    taille: { l: 1.2, p: 1.2 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  drapeau: {
    kind: 'drapeau',
    nom: 'Drapeau',
    icone: '🚩',
    cout: { bois: 6, fibre: 4 },
    taille: { l: 0.9, p: 0.9 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  panneau: {
    kind: 'panneau',
    nom: 'Panneau',
    icone: '🪧',
    cout: { bois: 4 },
    taille: { l: 1.4, p: 0.4 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  boite_lettres: {
    kind: 'boite_lettres',
    nom: 'Boîte aux lettres',
    feminin: true,
    icone: '📬',
    cout: { bois: 4, minerai: 2 },
    taille: { l: 0.7, p: 0.7 },
    habitable: false,
    couleurs: ['murs', 'toit'],
  },
  rampe_feux: {
    kind: 'rampe_feux',
    nom: 'Rampe de feux',
    feminin: true,
    icone: '🎆',
    cout: { bois: 4, pierre: 2, fibre: 2 },
    taille: { l: 1.5, p: 1.5 },
    habitable: false,
    couleurs: ['murs'],
  },
}

export function batimentDef(k: BatimentKind): BatimentDef {
  return BATIMENTS[k]
}

// --------------------------------------------------------------- categories

/**
 * Une rubrique du menu de construction.
 *
 * Le catalogue compte plus de cinquante modeles: une seule grille serait
 * illisible. Les rubriques sont donc la structure de reference de l'interface,
 * et `MAISONS` / `AMENAGEMENTS` n'en sont que des vues a plat.
 */
export interface CategorieBatiment {
  /** Identifiant stable, sans accent: sert de clef d'onglet et de journal. */
  id: string
  /** Titre affiche, accentue. */
  nom: string
  /** Pictogramme de la rubrique. */
  icone: string
  /** Modeles de la rubrique, dans l'ordre d'affichage. */
  modeles: readonly BatimentKind[]
}

/** Les habitables, dans l'ordre du menu (du plus accessible au plus cher). */
export const MAISONS: readonly BatimentKind[] = [
  'cabane_plage',
  'tente',
  'cabane',
  'igloo',
  'cabane_arbre',
  'chalet',
  'maisonnette',
  'grange',
  'villa',
  'villa_etage',
  'hotel',
  'phare',
]

/**
 * Les batiments du VILLAGE: on y entre, et chacun ouvre son propre jeu.
 *
 * Ils ne sont pas dans `MAISONS` parce qu'on n'y habite pas — pas de lit, pas
 * de cuisine (voir `AMEUBLEMENT` dans shared/sim/batiments.ts). Les melanger a
 * la liste des habitations donnait une rubrique de seize modeles ou l'ecole se
 * lisait comme une maison un peu chere.
 */
export const VILLAGE: readonly BatimentKind[] = [
  'ecole',
  'bibliotheque',
  'musee',
  'zoo',
  'parc_aquatique',
  'mine',
  'garage',
]

/**
 * LES AEROPORTS ont leur propre rubrique, et pas par coquetterie: ils sont les
 * seuls batiments dont on ait besoin AVANT de pouvoir batir autre chose (un
 * avion ne se pose que sur une piste). Les noyer parmi les huit modeles du
 * village aurait cache la seule chose qu'il faut comprendre pour voler.
 */
export const AEROPORTS: readonly BatimentKind[] = ['aerodrome', 'aeroport', 'aeroport_verre']

const EAU_JARDIN: readonly BatimentKind[] = [
  'jardin',
  'potager',
  'serre',
  'puits',
  'fontaine',
  'ruche',
  'piscine',
]

const CIRCULATION: readonly BatimentKind[] = [
  'chemin_pierre',
  'route',
  'cloture',
  'haie',
  'muret',
  'portail',
  'escalier',
  'pont_bois',
  'quai',
]

const MOBILIER: readonly BatimentKind[] = [
  'banc',
  'table_picnic',
  'chaise_longue',
  'parasol',
  'hamac',
  'balancoire',
  'toboggan',
  'bac_sable',
  'barbecue',
  'feu_camp',
]

const ECLAIRAGE: readonly BatimentKind[] = ['torche', 'lampadaire', 'guirlande', 'rampe_feux']

const UTILITAIRE: readonly BatimentKind[] = [
  'panneau',
  'boite_lettres',
  'coffre',
  'atelier',
  'four_pain',
  'epouvantail',
  'drapeau',
  'totem',
  'statue',
  'tour_guet',
  'moulin',
]

/** Les rubriques du menu de construction, dans l'ordre d'affichage. */
export const CATEGORIES_BATIMENT: readonly CategorieBatiment[] = Object.freeze([
  { id: 'maisons', nom: 'Maisons', icone: '🏠', modeles: MAISONS },
  { id: 'village', nom: 'Village', icone: '🏫', modeles: VILLAGE },
  { id: 'aviation', nom: 'Aéroports', icone: '✈️', modeles: AEROPORTS },
  { id: 'jardin', nom: 'Eau et jardin', icone: '🌷', modeles: EAU_JARDIN },
  { id: 'circulation', nom: 'Clôtures et chemins', icone: '🚧', modeles: CIRCULATION },
  { id: 'mobilier', nom: 'Mobilier de jardin', icone: '🪑', modeles: MOBILIER },
  { id: 'eclairage', nom: 'Éclairage', icone: '💡', modeles: ECLAIRAGE },
  { id: 'decor', nom: 'Utile et décor', icone: '🗿', modeles: UTILITAIRE },
])

/**
 * Les amenagements, dans l'ordre du menu: toutes les rubriques sauf celles ou
 * l'on entre (maisons et village).
 */
export const AMENAGEMENTS: readonly BatimentKind[] = [
  ...EAU_JARDIN,
  ...CIRCULATION,
  ...MOBILIER,
  ...ECLAIRAGE,
  ...UTILITAIRE,
]
